import { useMutation, useQuery } from '@tanstack/react-query'
import { reportService } from '../../services/reports/reportService'
import { showSuccessToast, showErrorToast } from '../../utils/errorHandler'
import { ReportParams } from '../../types'

export const useArAgingReport = (params?: { asOfDate: string }) => {
  return useQuery({
    queryKey: ['ar-aging-report', params],
    queryFn: () => reportService.getArAgingReport({ asOfDate: params!.asOfDate }),
    enabled: !!params?.asOfDate,
  })
}

export const useSalesTaxReport = (params?: ReportParams) => {
  return useQuery({
    queryKey: ['sales-tax-report', params],
    queryFn: () => reportService.getSalesTaxReport(params!),
    enabled: !!params?.startDate && !!params?.endDate,
  })
}

export const useGenerateArAgingExcel = () => {
  return useMutation({
    mutationFn: ({ asOfDate }: { asOfDate: string }) =>
      reportService.generateArAgingExcel(asOfDate),
    onSuccess: (data) => {
      if (data.file_url) {
        reportService.downloadReport(data.file_url)
        showSuccessToast(`A/R Aging Excel exported! (${data.storage === 's3' ? 'S3' : 'Local'})`)
      }
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useAdvanceTaxReport = (params?: ReportParams) => {
  return useQuery({
    queryKey: ['advance-tax-report', params],
    queryFn: () => reportService.getAdvanceTaxReport(params!),
    enabled: !!params?.startDate && !!params?.endDate,
  })
}

export const useGenerateExcelReport = () => {
  return useMutation({
    mutationFn: ({ startDate, endDate }: { startDate: string; endDate: string }) =>
      reportService.generateExcelReport(startDate, endDate),
    onSuccess: (data) => {
      if (data.file_url) {
        reportService.downloadReport(data.file_url)
        showSuccessToast(`Excel report generated successfully! (${data.storage === 's3' ? 'S3' : 'Local'})`)
      }
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const usePnlReport = (params?: ReportParams) => {
  return useQuery({
    queryKey: ['pnl-report', params],
    queryFn: () => reportService.getPnlReport(params!),
    enabled: !!params?.startDate && !!params?.endDate,
  })
}

export const useGeneratePnlExcel = () => {
  return useMutation({
    mutationFn: ({ startDate, endDate }: { startDate: string; endDate: string }) =>
      reportService.generatePnlExcel(startDate, endDate),
    onSuccess: (data) => {
      if (data.file_url) {
        reportService.downloadReport(data.file_url)
        showSuccessToast(`P&L Excel exported successfully! (${data.storage === 's3' ? 'S3' : 'Local'})`)
      }
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useBalanceSheetReport = (params?: { asOfDate: string }) => {
  return useQuery({
    queryKey: ['balance-sheet-report', params],
    queryFn: () => reportService.getBalanceSheetReport({ asOfDate: params!.asOfDate }),
    enabled: !!params?.asOfDate,
  })
}

export const useGenerateBalanceSheetExcel = () => {
  return useMutation({
    mutationFn: ({ asOfDate }: { asOfDate: string }) =>
      reportService.generateBalanceSheetExcel(asOfDate),
    onSuccess: (data) => {
      if (data.file_url) {
        reportService.downloadReport(data.file_url)
        showSuccessToast(`Balance Sheet Excel exported! (${data.storage === 's3' ? 'S3' : 'Local'})`)
      }
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}


export const useGenerateSalesTaxExcel = () => {
  return useMutation({
    mutationFn: ({ startDate, endDate }: { startDate: string; endDate: string }) =>
      reportService.generateSalesTaxExcel(startDate, endDate),
    onSuccess: (data) => {
      if (data.file_url) {
        reportService.downloadReport(data.file_url)
        showSuccessToast(`Sales Tax Excel exported successfully! (${data.storage === 's3' ? 'S3' : 'Local'})`)
      }
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}
